import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, ShoppingBagIcon } from 'lucide-react';
import { Product } from '../types';
import { formatPrice, stockLabel } from '../utils/format';
import { useCart } from '../contexts/CartContext';
interface ProductCardProps {
  product: Product;
  view?: 'grid' | 'list';
}
export function ProductCard({
  product,
  view = 'grid'
}: ProductCardProps) {
  const {
    addItem
  } = useCart();
  const [added, setAdded] = React.useState(false);
  const stock = stockLabel(product.stock);
  const soldOut = product.stock === 0;
  const price = product.salePrice ?? product.price;
  const handleAdd = () => {
    if (soldOut) return;
    addItem(product.id, product.colors[0]?.id ?? 'black');
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1600);
  };
  const badges = <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5" data-id="element-657" data-mp="2v5ptfsk34dy2">
      {product.salePrice && <span className="bg-pink-500 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white" data-id="element-658" data-mp="304iwss5nqoki">
          Oferta
        </span>}
      {product.isNew && <span className="bg-teal-500 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-black" data-id="element-659" data-mp="3gzvnx3f90rxb">
          Nuevo
        </span>}
    </div>;
  const addButton = <button type="button" onClick={handleAdd} disabled={soldOut} className={`flex w-full items-center justify-center gap-2 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.16em] transition-colors ${soldOut ? 'cursor-not-allowed bg-ink-600 text-white/35' : added ? 'bg-teal-500 text-black' : 'bg-pink-500 text-white hover:bg-pink-600'}`} data-id="element-660" data-mp="37zpfhvyeqi50">
      {soldOut ? 'Sin stock' : added ? <>
          <CheckIcon className="h-4 w-4" data-id="element-661" data-mp="2kaeb84hum5m4" /> Agregado
        </> : <>
          <ShoppingBagIcon className="h-4 w-4" data-id="element-662" data-mp="2fb1orkuw30k2" /> Agregar al carrito
        </>}
    </button>;
  if (view === 'list') {
    return <article className="group flex flex-col gap-5 border border-white/10 bg-ink-800 p-4 transition-all duration-300 hover:border-pink-500/60 hover:shadow-pink sm:flex-row" data-id="element-663" data-mp="2gt8pdy9ad1yp">
        <Link to={`/producto/${product.slug}`} className="relative block w-full shrink-0 overflow-hidden bg-black sm:w-48" data-id="element-664" data-mp="27s26jij5zf2a">
          <img src={product.images[0]} alt={product.name} className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-full" data-id="element-665" data-mp="2csgd0w54agy7" />
          {badges}
        </Link>
        <div className="flex flex-1 flex-col" data-id="element-666" data-mp="1p1kfrjhw6tjk">
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-pink-400" data-id="element-667" data-mp="1oyohtv61o4v4">
            {product.categoryLabel}
          </span>
          <h3 className="mt-2 font-display text-2xl uppercase leading-none tracking-wide text-white" data-id="element-668" data-mp="1f2bpp1q5dz2h">
            <Link to={`/producto/${product.slug}`} className="hover:text-pink-400" data-id="element-669" data-mp="1k1y4hflmjnde">
              {product.name}
            </Link>
          </h3>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/50" data-id="element-670" data-mp="10hz6z9px3q7k">
            {product.shortDescription}
          </p>
          <div className="mt-3 flex items-center gap-2 text-[11px]" data-id="element-671" data-mp="1adsu1n19j23l">
            <span className={`h-2 w-2 rounded-full ${stock.dot}`} aria-hidden data-id="element-672" data-mp="1abzlu3b049th" />
            <span className={stock.text} data-id="element-673" data-mp="15deku1nxdm3z">{stock.label}</span>
          </div>
          <div className="mt-auto flex flex-wrap items-end justify-between gap-4 pt-5" data-id="element-674" data-mp="1jdpbp4p8j0p5">
            <div className="flex items-baseline gap-2" data-id="element-675" data-mp="1egfq604yv96l">
              <span className="font-display text-3xl leading-none text-white" data-id="element-676" data-mp="1edtim0i2stod">
                {formatPrice(price)}
              </span>
              {product.salePrice && <span className="text-sm text-white/35 line-through" data-id="element-677" data-mp="1o5z5wwxdumx8">
                  {formatPrice(product.price)}
                </span>}
            </div>
            <div className="w-full sm:w-56" data-id="element-678" data-mp="1y4ogs5qv373z">{addButton}</div>
          </div>
        </div>
      </article>;
  }
  return <article className="group flex h-full flex-col border border-white/10 bg-ink-800 transition-all duration-300 hover:-translate-y-1.5 hover:border-pink-500/60 hover:shadow-pink" data-id="element-679" data-mp="3rluf3zxdphl4">
      <Link to={`/producto/${product.slug}`} className="relative block overflow-hidden bg-black" data-id="element-680" data-mp="57udl5j760lt">
        <img src={product.images[0]} alt={product.name} className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-681" data-mp="a52tezgvhsjw" />
        {badges}
      </Link>
      <div className="flex flex-1 flex-col p-5" data-id="element-682" data-mp="3ilp99u7u2r92">
        <div className="flex items-center justify-between gap-2" data-id="element-683" data-mp="3ikwapgj13820">
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-pink-400" data-id="element-684" data-mp="3mn0zo1yj7gib">
            {product.categoryLabel}
          </span>
          <span className="flex items-center gap-1.5 text-[11px]" data-id="element-685" data-mp="xux9dpwhnx8d">
            <span className={`h-2 w-2 rounded-full ${stock.dot}`} aria-hidden data-id="element-686" data-mp="11wa9rkgtb87w" />
            <span className={stock.text} data-id="element-687" data-mp="2meuk33z39bgv">{stock.label}</span>
          </span>
        </div>
        <h3 className="mt-2.5 font-display text-2xl uppercase leading-none tracking-wide text-white" data-id="element-688" data-mp="2idr7cqcc7vjv">
          <Link to={`/producto/${product.slug}`} className="hover:text-pink-400" data-id="element-689" data-mp="2dmiz3xmqug8a">
            {product.name}
          </Link>
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-white/45" data-id="element-690" data-mp="28mwj5gfj6lvs">
          {product.shortDescription}
        </p>
        <div className="mt-4 flex items-baseline gap-2" data-id="element-691" data-mp="23pwdv7qi9n5q">
          <span className="font-display text-3xl leading-none text-white" data-id="element-692" data-mp="1yp08epqi9sy7">
            {formatPrice(price)}
          </span>
          {product.salePrice && <span className="text-sm text-white/35 line-through" data-id="element-693" data-mp="1uprijvyl6f7x">
              {formatPrice(product.price)}
            </span>}
        </div>
        <div className="mt-5 flex-1" data-id="element-694" data-mp="3p0wj2redmbj1" />
        {addButton}
      </div>
    </article>;
}